import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, HStack, VStack, Heading, Progress } from '@chakra-ui/react';
import styles from '../../styles';
import { Card, CardBody } from 'components/v4/Card';
import Text from 'components/v4/Text';
import { MenuItem } from 'components/v4';
import Image from 'components/v4/Image';
import DashboardCardEditButton from '../../components/DashboardCardEditButton';
import CardDetails from '../../components/CardDetails';
import ServiceDashboardItemSyncUsers from 'components/v4/Services/ServiceDashboard/ServiceDashboardItemSyncUsers';
import { useServicesDashboard } from 'context/ServicesDashboardContext';
import ServiceStatus from './ServiceStatus';
import { IconComponent } from '../../components/IconComponent';
import { dateStringToDate } from 'utils/utils';
import {
    isSyncEnabledServiceType,
    canEditService,
    doesSetSelectedServiceProfile,
} from 'pages/dashboards/v5/constants';
import OCMenuItems from '../../components/OperatorConnect/OCMenuItems';
import { GeneratePermission } from 'pages/dashboards/v5/components/GeneratePermission';
import GCCHMenuItems from '../../components/GCCH/GCCHMenuItems';
import EightByEightMenuItems from '../../components/EightByEight/EightByEightMenuItems';
import DashboardCardSkeleton from './DashboardCardSkeleton';
import { getEightByEightFallbackStatus } from '../../components/helpers/cardStatusHelper';
import {
    MICROSOFT_GRAPH,
    APP_AUTH,
    EIGHTBYEIGHT,
    OPERATOR_CONNECT,
    DIRECT_ROUTING,
    ZOOM,
    GCC_HIGH,
    WEBEX,
    RINGCENTRAL,
    DIALPAD,
} from '../../constants';
import EditServiceMenuItem from 'pages/dashboards/v5/components/EditServiceMenuItem';
import {
    getSimpleRedirectPlatformByServiceType,
    getSimpleRedirectPlatformByWizardServiceType,
} from 'services/platformRegistry';

const DashboardCard = ({ service, serviceWizard, companyAppAuth }) => {
    const { darkMode } = useSelector((state) => state.settings);
    const serviceStatus = useServicesDashboard();
    const isMicrosoftAppAuth = service?.serviceType === MICROSOFT_GRAPH;
    const isOCService = service?.serviceType === OPERATOR_CONNECT;
    const isDRService = service?.serviceType === DIRECT_ROUTING;
    const isGCCHService = service?.serviceType === GCC_HIGH;
    const isEightByEightService = service?.serviceType === EIGHTBYEIGHT;
    const isWebExService = service?.serviceType === WEBEX;
    const isZoomService = service?.serviceType === ZOOM;
    const isRingCentralService = service?.serviceType === RINGCENTRAL;
    const isDialPadService = service?.serviceType === DIALPAD;

    const rawCardStatus =
        serviceStatus[isMicrosoftAppAuth ? APP_AUTH : service?.serviceType];
    // 8x8 reducer status is only trustworthy when it has a statusLabel and was
    // resolved for this service; otherwise fall back to the list statusDisplay.
    const eightStatusFresh =
        rawCardStatus?.statusLabel && rawCardStatus?.serviceId === service?.id;
    const cardStatus =
        isEightByEightService && !eightStatusFresh && service?.statusDisplay
            ? getEightByEightFallbackStatus(service?.statusDisplay)
            : rawCardStatus;
    const accountScope = useSelector((state) => state.login.requiredScope);
    const [currentCompanyAppAuth, setCurrentCompanyAppAuth] = useState(null);

    const [actionPermissions, setActionPermissions] = useState({
        menuEditServiceScope: 40,
        menuRequestScope: 60,
    });

    const [data, setData] = useState({});
    const handleGrantAccess = async () => {
        // Zoom/8x8/Webex/RingCentral/DialPad all just redirect to a login link —
        // see services/platformRegistry.js. Any other platform's Grant Access
        // needs its own real conditional flow, handled below.
        // FIX:(3178): the Zoom card's service is 'ZoomPEX' (the registry's
        // wizardServiceType), not its 'Zoom' serviceType.
        if (
            getSimpleRedirectPlatformByServiceType(service?.serviceType) ??
            getSimpleRedirectPlatformByWizardServiceType(service?.serviceType)
        ) {
            await serviceWizard.grantAccessForServiceType(service?.serviceType);
            return;
        }
        switch (service?.serviceType) {
            case OPERATOR_CONNECT:
                if (service?.company?.selectiveSyncPolicy === 2) {
                    if (serviceWizard.msServiceAuth?.id) {
                        await serviceWizard.microsoftAuth();
                    } else {
                        serviceWizard.ocFlow();
                    }
                } else if (
                    service?.company?.selectiveSyncPolicy === 1 ||
                    service?.company?.selectiveSyncPolicy === 0
                ) {
                    await serviceWizard.microsoftAuth();
                }
                break;
            case MICROSOFT_GRAPH:
                await serviceWizard.microsoftAuth();
                break;
            case GCC_HIGH:
                if (service?.company?.selectiveSyncPolicy === 2) {
                    if (serviceWizard.msServiceAuth?.id) {
                        await serviceWizard.gcchAuth();
                    } else {
                        serviceWizard.drFlow(service);
                    }
                } else if (
                    service?.company?.selectiveSyncPolicy === 1 ||
                    service?.company?.selectiveSyncPolicy === 0
                ) {
                    await serviceWizard.gcchAuth();
                }
                break;
            case DIRECT_ROUTING:
                if (service?.company?.selectiveSyncPolicy === 2) {
                    if (serviceWizard.msServiceAuth?.id) {
                        await serviceWizard.microsoftAuth();
                    } else {
                        serviceWizard.drFlow(service);
                    }
                } else if (
                    service?.company?.selectiveSyncPolicy === 1 ||
                    service?.company?.selectiveSyncPolicy === 0
                ) {
                    await serviceWizard.microsoftAuth();
                }
                break;
        }
    };

    const hasSimpleGrantAccessCardStatus =
        (isEightByEightService || isRingCentralService || isDialPadService) &&
        cardStatus?.grantAccess;
    const hasGrantAccess =
        serviceWizard !== undefined &&
        service?.authPolicy !== 0 && // 0: Disabled
        ((isZoomService &&
            !serviceWizard?.ZoomAppAuthService &&
            // FIX:(2725): Grant Access missing from Zoom App Auth
            cardStatus?.grantAccess) ||
            hasSimpleGrantAccessCardStatus ||
            (isDRService &&
                cardStatus?.grantAccess &&
                service?.authPolicy !== 0) ||
            (isGCCHService &&
                cardStatus?.grantAccess &&
                service?.authPolicy !== 0) ||
            (isWebExService &&
                !serviceWizard?.WebexAppAuthService &&
                cardStatus?.grantAccess));

    useEffect(() => {
        if (service && doesSetSelectedServiceProfile(service?.serviceType)) {
            serviceWizard?.setSelectedServiceProfile(service);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [service]);

    useEffect(() => {
        if (companyAppAuth) {
            //set MS Service Auth & Service Data.
            setData({
                ...serviceWizard.msServiceAuth,
                ...service,
                userSync: companyAppAuth?.userSync,
                hasAppAuthData: true,
                lastProcessed:
                    serviceWizard.msServiceAuth?.lastProcessedTS &&
                    dateStringToDate(
                        serviceWizard.msServiceAuth?.lastProcessedTS,
                        'datetime',
                    ),
            });
            setCurrentCompanyAppAuth(companyAppAuth);
        }
    }, [companyAppAuth, service, serviceWizard?.msServiceAuth]);
    useEffect(() => {
        if (service) {
            if (
                !serviceWizard?.msServiceAuth?.tenantID &&
                service?.status === 2 &&
                (service?.authPolicy === 0 || service?.authPolicy === 1)
            ) {
                // Tenant ID
                if (service?.company?.selectiveSyncPolicy !== 2) {
                    setActionPermissions((p) => ({
                        ...p,
                        ...{
                            menuTenantIdScope: 20,
                        },
                    }));
                }
            } else {
                setActionPermissions((p) => {
                    if (
                        Object.prototype.hasOwnProperty.call(
                            p,
                            'menuTenantIdScope',
                        )
                    ) {
                        delete p.menuTenantIdScope;
                        return p;
                    }
                    return p;
                });
            }

            // Provide Access
            if (service?.authPolicy !== 0) {
                if (
                    (service?.status === 2 && currentCompanyAppAuth === null) ||
                    (service?.status === 2 &&
                        (data.userSync === false ||
                            data.graphAuth === false)) ||
                    currentCompanyAppAuth?.status === 20
                ) {
                    setActionPermissions((p) => ({
                        ...p,
                        ...{
                            menuProvideAccessScope: 20,
                        },
                    }));
                }
            } else {
                setActionPermissions((p) => {
                    if (
                        Object.prototype.hasOwnProperty.call(
                            p,
                            'menuProvideAccessScope',
                        )
                    ) {
                        delete p.menuProvideAccessScope;
                        return p;
                    }
                    return p;
                });
            }

            //TODO: Enable User Sync After Manual App Auth

            if (service?.authPolicy !== 0) {
                if (
                    serviceWizard?.msServiceAuth?.tenantID &&
                    data.graphAuth === false && //have tenant id & no graph auth
                    data.userSync === false // hide if userSync is enabled
                ) {
                    setActionPermissions((p) => ({
                        ...p,
                        ...{
                            menuEnableSyncScope: 20,
                        },
                    }));
                }
            } else {
                setActionPermissions((p) => {
                    if (
                        Object.prototype.hasOwnProperty.call(
                            p,
                            'menuEnableSyncScope',
                        )
                    ) {
                        delete p.menuEnableSyncScope;
                        return p;
                    }
                    return p;
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCompanyAppAuth, companyAppAuth, service, data]);

    if (
        !cardStatus ||
        (!cardStatus.statusLabel && cardStatus.color === 'gray')
    ) {
        return <DashboardCardSkeleton />;
    }

    // Fix OC empty menu item case
    const hasOCMenuItems =
        (isOCService && accountScope >= actionPermissions?.menuRequestScope) ||
        (cardStatus?.showTenantId &&
            service?.authPolicy !== 2 &&
            cardStatus?.grantAccess &&
            service?.authPolicy !== 0);
    const hasEditService = isOCService
        ? hasOCMenuItems && canEditService(OPERATOR_CONNECT)
        : canEditService(service?.serviceType);
    const hasRequestResource =
        accountScope >= actionPermissions?.menuRequestScope;
    // 8x8: Manage Licences is shown for all statuses at Company+ scope
    const has8x8MenuItems = isEightByEightService && accountScope >= 20;
    const menuButtonVisible =
        hasGrantAccess ||
        hasEditService ||
        hasRequestResource ||
        cardStatus?.showActivationMenu ||
        has8x8MenuItems;

    return (
        <Card
            bg={darkMode ? 'dark.tcap' : null}
            sx={{
                minHeight: '190px',
                minWidth: '250px',
                border: '1px solid',
                borderColor: darkMode ? 'gray.700' : 'gray.200',
                boxShadow: darkMode
                    ? 'none'
                    : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            }}>
            <Progress
                value={100}
                sx={styles.verticalBar}
                colorScheme={cardStatus?.color}
                hasStripe={cardStatus?.animation}
                isAnimated={cardStatus?.animation}
            />
            <CardBody
                sx={{
                    display: 'flex',
                    flexDir: 'column',
                }}>
                <VStack
                    alignItems="flex-start"
                    width="100%"
                    spacing={2}
                    marginBottom="15px">
                    <HStack
                        alignItems="flex-start"
                        width="100%"
                        overflow="hidden">
                        <Image
                            className={`${isEightByEightService ? 'h-[50px] w-[50px] rounded-full' : 'rounded-xl'}`}
                            src={CardDetails(service?.serviceType)?.icon}
                            sx={{ maxWidth: '50px', flexShrink: 0 }}
                        />
                        <VStack alignItems="flex-start" width="100%" minW={0}>
                            <Box width="100%" minH="3rem">
                                <Heading as="h4" size="md" noOfLines={2}>
                                    {
                                        CardDetails(service?.serviceType)
                                            ?.serviceName
                                    }
                                </Heading>
                            </Box>
                            {/* Status */}
                            <Box
                                className="flex justify-between"
                                sx={{ paddingBottom: '20px', width: '100%' }}>
                                <ServiceStatus
                                    service={service}
                                    cardStatus={cardStatus}
                                    handleActions={{ handleGrantAccess }}
                                    serviceObj={service}
                                />
                            </Box>
                            {/* Channels */}
                            {data?.sipTrunks && (
                                <Box
                                    className="mb-[15px] flex justify-between"
                                    style={{ width: '60%' }}>
                                    <Text className="font-medium">
                                        SIP Trunks:
                                    </Text>
                                    <Text margin={0}>{data?.sipTrunks}</Text>
                                </Box>
                            )}
                            {/* Max Channel */}
                            {data?.maxChannel && (
                                <Box
                                    className="mb-[15px] flex justify-between"
                                    style={{ width: '60%' }}>
                                    <Text className="font-medium">
                                        Max Channel:
                                    </Text>
                                    <Text margin={0}>{data?.maxChannel}</Text>
                                </Box>
                            )}

                            {/* Sync */}
                            {isSyncEnabledServiceType(service?.serviceType) && (
                                <Box
                                    className="mb-[15px] flex justify-between"
                                    style={{ width: '60%' }}>
                                    <Text className="font-medium">Sync:</Text>
                                    <Text margin={0}>
                                        <ServiceDashboardItemSyncUsers
                                            data={data}
                                        />
                                    </Text>
                                </Box>
                            )}

                            {/* 8x8 Numbers — each row waits for a real count
                                rather than defaulting to 0, so nothing is shown
                                until the backend supplies the field. */}
                            {isEightByEightService && service && (
                                <>
                                    {typeof service?.availableNumbers ===
                                        'number' && (
                                        <Box
                                            className="mb-[15px] flex justify-between"
                                            style={{ width: '60%' }}>
                                            <Text className="font-medium">
                                                Available Numbers:
                                            </Text>
                                            <Text margin={0}>
                                                {service.availableNumbers}
                                            </Text>
                                        </Box>
                                    )}
                                    {typeof service?.activeNumbers ===
                                        'number' && (
                                        <Box
                                            className="mb-[15px] flex justify-between"
                                            style={{ width: '60%' }}>
                                            <Text className="font-medium">
                                                Active Numbers:
                                            </Text>
                                            <Text margin={0}>
                                                {service.activeNumbers}
                                            </Text>
                                        </Box>
                                    )}
                                </>
                            )}

                            {/* <DashboardItemLastHealthCheck data={data} /> */}
                        </VStack>
                    </HStack>
                </VStack>
                {menuButtonVisible && (
                    <DashboardCardEditButton>
                        {/* Consistent order across service types: Edit
                            Service, Tenant ID, Request Access, Grant Access. */}
                        {hasEditService && (
                            <EditServiceMenuItem
                                accountScope={accountScope}
                                actionPermissions={actionPermissions}
                                cardStatus={cardStatus}
                                darkMode={darkMode}
                                service={service}
                                serviceObj={service}
                            />
                        )}
                        {isOCService && (
                            <OCMenuItems
                                section="tenantId"
                                cardStatus={cardStatus}
                                darkMode={darkMode}
                                handleGrantAccess={handleGrantAccess}
                                service={service}
                                serviceObj={service}
                                serviceWizard={serviceWizard}
                            />
                        )}

                        {/* Request Resource, NOTE: only for OC, DR and GCCH */}
                        {(isOCService || isDRService || isGCCHService) &&
                            hasRequestResource &&
                            // (3150): only hide it for a healthy OC service when
                            // authPolicy 0 (Disabled) actually disallows sync —
                            // any other policy should still allow requesting access.
                            !(
                                isOCService &&
                                service?.status === 0 &&
                                service?.authPolicy === 0
                            ) && (
                                <GeneratePermission
                                    accountScope={accountScope}
                                    isGCCHService={isGCCHService}
                                    serviceObj={service}
                                />
                            )}
                        {hasGrantAccess && (
                            <MenuItem
                                id="oc-provide-access"
                                icon={
                                    <IconComponent
                                        icon="Settings"
                                        color={darkMode ? 'white' : 'black'}
                                    />
                                }
                                onClick={handleGrantAccess}
                                data-walkthroughid={`/dashboard-services/card/${service.name}/provide-access`}>
                                <Text marginBottom="0">Grant Access</Text>
                            </MenuItem>
                        )}
                        {isOCService && (
                            <OCMenuItems
                                section="grantAccess"
                                cardStatus={cardStatus}
                                darkMode={darkMode}
                                handleGrantAccess={handleGrantAccess}
                                service={service}
                                serviceObj={service}
                                serviceWizard={serviceWizard}
                            />
                        )}
                        {isGCCHService && (
                            <GCCHMenuItems
                                cardStatus={cardStatus}
                                darkMode={darkMode}
                                service={service}
                            />
                        )}
                        {isEightByEightService && (
                            <EightByEightMenuItems
                                cardStatus={cardStatus}
                                darkMode={darkMode}
                                service={service}
                                serviceObj={service}
                            />
                        )}
                    </DashboardCardEditButton>
                )}
            </CardBody>
        </Card>
    );
};

export default DashboardCard;
